import React, { useState, useEffect } from "react";
import { array, func } from "prop-types";
import isValid from "date-fns/isValid";

import SearchBar from "../../atoms/search-bar";
import SearchButton from "../../atoms/search-button";
import CustomSuggestionList from "../../atoms/custom-suggestion-list";
import CustomCalendar from "../../atoms/calendar";
import CustomDropdown from "../../molecules/custom-dropdown";
import PriceRange from "../price-range";

import "./custom-filters.m.css";

const currentDate = new Date();

const CustomFilters = ({ data, searchData, onUpdateFilters }) => {
  const [filteredSearchData, setFilteredSearchData] = useState(searchData);
  const [date, setDate] = useState([currentDate, currentDate]);
  const [dropDownValue, setDropDownValue] = useState("null");
  const [calendarVisible, toggleCalendar] = useState(false);
  const [isSearchBarVisible, toggleSearchBar] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isRangeSet, setRangeSet] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  useEffect(
    () => {
      setFilteredSearchData(searchData);
      setDate([currentDate, currentDate]);
      setDropDownValue("null");
      toggleCalendar(false);
      toggleSearchBar(false);
      setIsSearching(false);
      setRangeSet(false);
      setSearchValue("");
    },
    [data]
  );

  const onClickApply = () => {
    toggleCalendar(false);
    toggleSearchBar(false);
    setIsSearching(false);
    setRangeSet(false);

    if (date.length === 2) {
      const startDate = date[0];
      const endDate = date[1];
      const filteredData = data.filter(item => {
        const inspectionStartDateTime = new Date(item["inspection-start-date-time"]);
        const inspectionEndDateTime = new Date(item["inspection-end-date-time"]);
        if (isValid(inspectionStartDateTime) && isValid(inspectionEndDateTime)) {
          return (
            inspectionStartDateTime.getTime() >= startDate.getTime() &&
            inspectionEndDateTime.getTime() <= endDate.getTime()
          );
        }
        if (isValid(inspectionStartDateTime)) {
          return (
            inspectionStartDateTime.getTime() >= startDate.getTime() &&
            inspectionStartDateTime.getTime() <= endDate.getTime()
          );
        }
        if (isValid(inspectionEndDateTime)) {
          return (
            inspectionEndDateTime.getTime() <= startDate.getTime() &&
            inspectionEndDateTime.getTime() <= endDate.getTime()
          );
        }
        return false;
      });
      onUpdateFilters(filteredData);
    }
  };

  const onChangeOptions = ({ target: { value } }) => {
    setDropDownValue(value);
    toggleSearchBar(false);
    setIsSearching(false);
    setRangeSet(false);
    toggleCalendar(false);

    if (value === "custom") {
      toggleCalendar(true);
    } else if (value === "reset") {
      global.app.navigateToPage(() => {}, `/housing-auction`);
    }
  };

  const onClickSearch = () => {
    setDropDownValue("null");
    toggleCalendar(false);
    setRangeSet(false);
    toggleSearchBar(true);

    setIsSearching(true);
    const listResult = searchData.filter(item => item.id === listItem.id);
    setFilteredSearchData(listResult);

    const filteredData = data.filter(item =>item["auction-location"] !== undefined  && item["auction-location"].id === listItem.id);
    onUpdateFilters(filteredData);
  };

  const renderCalendar = () => (
    <CustomCalendar onChange={setDate} onClickApply={onClickApply} value={date} selectRange={true} />
  );

  const searchLocations = ({ target }) => {
    setDropDownValue("null");
    toggleCalendar(false);
    setRangeSet(false);
    setSearchValue(target.value);

    const searchString = target.value.trim().toLowerCase();

    if (!searchString) {
      setIsSearching(false);
      setFilteredSearchData(searchData);
      onUpdateFilters(data);
      return;
    }

    setIsSearching(true);

    const searchResult = searchData.filter(item => item.name.toLowerCase().includes(searchString));
    setFilteredSearchData(searchResult);
  };

  const onClickList = listItem => {
    setDropDownValue("null");
    toggleCalendar(false);
    setRangeSet(false);
    setSearchValue(listItem.name);

    setIsSearching(true);
    const listResult = searchData.filter(item => item.id === listItem.id);
    setFilteredSearchData(listResult);

    const filteredData = data.filter(item =>item["auction-location"] !== undefined  && item["auction-location"].id === listItem.id);
    onUpdateFilters(filteredData);
  };

  const onChangePrice = values => {
    setDropDownValue("null");
    toggleCalendar(false);
    toggleSearchBar(false);
    setIsSearching(false);
    setRangeSet(true);

    if (values.length > 0) {
      const start = parseFloat(values[0]);
      const end = parseFloat(values[1]);
      const filteredData = data.filter(
        item => parseFloat(item["reserved-price"]) >= start && parseFloat(item["reserved-price"]) <= end
      );
      onUpdateFilters(filteredData);
    } else {
      onUpdateFilters(data);
    }
  };

  return (
    <div styleName="wrapper">
      {/* Desktop view */}
      <div styleName="desktop-view">
        <SearchBar placeholder="Search Locations..." value={searchValue} onChange={searchLocations} />
        <CustomSuggestionList data={filteredSearchData} onClick={onClickList} />
        <CustomDropdown
          value={dropDownValue}
          onChangeOptions={onChangeOptions}
          isSearchBarVisible={isSearchBarVisible}
        />
        {calendarVisible && renderCalendar()}
        <PriceRange isRangeSet={isRangeSet} onChange={onChangePrice} />
      </div>

      {/* Mobile view */}
      <div styleName="mobile-view">
        <div styleName="filters">
          <CustomDropdown value={dropDownValue} onChangeOptions={onChangeOptions} />
          <SearchButton title="LOCATION" onClick={onClickSearch} />
        </div>
        <div styleName="search-bar">
          {calendarVisible && renderCalendar()}
          {isSearchBarVisible && (
            <SearchBar placeholder="Search Locations..." value={searchValue} onChange={searchLocations} />
          )}
          {isSearchBarVisible &&
            isSearching && (
              <CustomSuggestionList data={filteredSearchData} isSearching={isSearching} onClick={onClickList} />
            )}
        </div>
        <div>
          <PriceRange isRangeSet={isRangeSet} onChange={onChangePrice} />
        </div>
      </div>
    </div>
  );
};

CustomFilters.propTypes = {
  data: array,
  searchData: array,
  onUpdateFilters: func
};

export default CustomFilters;
